import { createRouter, createWebHistory } from 'vue-router';
import {
  ROLE_MANAGE_AWA_ADMIN,
  ROLE_MANAGE_PLATFORM_ADMIN,
  type FeatureFlag,
} from '@manage/shared-types';
import { ROUTES, ROUTE_SEGMENTS } from '@/constants/routes';
import { useAuthStore } from '../stores/auth';
import { useFeatureFlagsStore } from '../stores/featureFlags';
import HomeView from '../views/HomeView.vue';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    requiredRoles?: string[];
    featureFlag?: FeatureFlag;
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: ROUTES.EDUCATION,
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('../views/LoginView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/auth/callback',
      name: 'AuthCallback',
      component: () => import('../views/AuthCallbackView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: ROUTES.EDUCATION,
      component: () => import('@/views/EducationLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          redirect: ROUTES.EDUCATION_INDIVIDUALS,
        },
        {
          // No requiredRoles — AWA admins keep access.
          path: ROUTE_SEGMENTS.INDIVIDUALS,
          name: 'EducationIndividuals',
          component: HomeView,
        },
        {
          // AWA admins (no ROLE_MANAGE_PLATFORM_ADMIN) are redirected to Individuals — hidden+redirect, mirroring the Workforce tab.
          path: ROUTE_SEGMENTS.DISTRIBUTORS,
          name: 'EducationDistributors',
          component: () => import('@/views/EducationDistributorsView.vue'),
          meta: { requiredRoles: [ROLE_MANAGE_PLATFORM_ADMIN] },
        },
        {
          path: ROUTE_SEGMENTS.REPORTS,
          name: 'EducationReports',
          component: () => import('@/views/EducationReportsView.vue'),
          meta: {
            requiredRoles: [ROLE_MANAGE_PLATFORM_ADMIN, ROLE_MANAGE_AWA_ADMIN],
            featureFlag: 'reports',
          },
        },
        {
          // Platform-admin only — same hidden+redirect gating as Distributors.
          path: ROUTE_SEGMENTS.ACCESS_KEYS,
          name: 'EducationAccessKeys',
          component: () => import('@/views/EducationAccessKeysView.vue'),
          meta: { requiredRoles: [ROLE_MANAGE_PLATFORM_ADMIN] },
        },
      ],
    },
    {
      path: '/workforce/:pathMatch(.*)*',
      name: 'Workforce',
      component: () => import('@/views/WorkforceView.vue'),
      props: (route) => ({
        initialPath: route.params.pathMatch
          ? `/${(route.params.pathMatch as string[]).join('/')}`
          : undefined,
      }),
      meta: {
        requiresAuth: true,
        requiredRoles: [ROLE_MANAGE_PLATFORM_ADMIN],
        featureFlag: 'workforce',
      },
    },
  ],
});

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore();

  if (!authStore.initialized) {
    try {
      await authStore.initializeAuth();
    } catch (err) {
      console.error('[Router] Auth initialization failed:', err);
    }
  }

  if (to.name === 'AuthCallback') {
    return next();
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return next('/login');
  }

  if (to.meta.requiredRoles && authStore.user) {
    const requiredRoles = to.meta.requiredRoles;
    const userRoles = authStore.user.roles || [];
    const hasRequiredRole = requiredRoles.some((role) => userRoles.includes(role));

    if (!hasRequiredRole) {
      console.warn(
        `[Router] Access denied to ${to.path}. Required roles: ${requiredRoles.join(', ')}`
      );

      return next({ path: ROUTES.EDUCATION_INDIVIDUALS, query: { accessDenied: to.path } });
    }
  }

  // Feature flag guard: redirect disabled features to /education/individuals
  // NOTE: Assumes user is authenticated (fetchFlags() calls API). This works because
  // requiresAuth routes redirect first. A route with featureFlag but no requiresAuth
  // would 401. All current flagged routes (e.g., /workforce) require auth.
  if (to.meta.featureFlag) {
    const featureFlagsStore = useFeatureFlagsStore();

    if (!featureFlagsStore.loaded) {
      await featureFlagsStore.fetchFlags();
    }

    if (!featureFlagsStore.isEnabled(to.meta.featureFlag)) {
      return next(ROUTES.EDUCATION_INDIVIDUALS);
    }
  }

  if (to.name === 'Login' && authStore.isAuthenticated) {
    return next(ROUTES.EDUCATION_INDIVIDUALS);
  }

  return next();
});

export default router;
